import * as lodash from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { ErrorStateMatcher } from '@angular/material/core';
import { FloatLabelType } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';

import { AutocompleteOption } from '../../interfaces';
import { ParentControlErrorStateMatcher } from '../../utilities';

@Component({
  selector: 'phoenix-autocomplete',
  template: `
    @if (!loadingList) {
      <mat-form-field [floatLabel]="floatLabelStatus" class="w-full">
        <mat-label>{{ title }}</mat-label>

        <input
          #autocompleteInput
          type="text"
          matInput
          [name]="title.replace(' ', '').toLocaleLowerCase()"
          [attr.aria-label]="
            title.replace(' ', '').toLocaleLowerCase() + 'Autocomplete'
          "
          [formControl]="control"
          [errorStateMatcher]="errorMatcher!"
          (input)="filter()"
          (focus)="filter()"
          (blur)="onBlur()"
          (paste)="paste($event)"
          (keydown.enter)="onEnter($event)"
          [matAutocomplete]="auto"
        />

        <!-- Error Messages -->
        @if (control.errors && errorMessages) {
          <mat-error>
            <small>{{ errorMessages[errorKey] }}</small>
          </mat-error>
        } @else if (control.errors) {
          <mat-error>
            <small>{{ control | formError }}</small>
          </mat-error>
        } @else if (parentControl?.errors) {
          <mat-error>
            <small>{{
              errorMessages?.[parentErrorKey] ?? (parentControl! | formError)
            }}</small>
          </mat-error>
        }

        <!-- Hints -->
        @if (hintTemplate) {
          <mat-hint>
            <ng-container *ngTemplateOutlet="hintTemplate"></ng-container>
          </mat-hint>
        }
        <mat-autocomplete
          #auto="matAutocomplete"
          [autoActiveFirstOption]="!freeText"
          [requireSelection]="!freeText"
          [displayWith]="displayValue"
          [hideSingleSelectionIndicator]="hideSingleSelectionIndicator"
          (optionSelected)="optionSelected($event)"
        >
          @for (option of filteredOptions; track option) {
            <mat-option
              [disabled]="isReadOnly"
              [value]="attrToSave ? option[attrToSave] : option"
            >
              @if (optionHtml) {
                <div class="w-full" [innerHTML]="optionHtml(option)"></div>
              } @else {
                {{
                  applyTitleCase
                    ? (displayValue(option) | titlecase)
                    : displayValue(option)
                }}
              }
            </mat-option>
          }
          @if (showNoOptionsMessage) {
            <mat-option disabled>
              {{ noOptionsMessage }}
            </mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>
    } @else {
      <ngx-skeleton-loader />
    }
  `,
  standalone: false,
})
export class AutocompleteComponent implements OnChanges {
  @ViewChild('autocompleteInput') autocompleteInput!: ElementRef;
  @ViewChild(MatAutocompleteTrigger)
  autocompleteTrigger!: MatAutocompleteTrigger;

  // Syncs the parent control's required state to the Material input marker.
  private _matInput?: MatInput;
  @ViewChild(MatInput) set matInput(input: MatInput | undefined) {
    this._matInput = input;
    this.syncRequiredMarker();
  }
  get matInput(): MatInput | undefined {
    return this._matInput;
  }
  @Output() valueChange = new EventEmitter();
  @Input() attrFilter!: string | null;
  @Input() attrToSave!: string | null;
  @Input() customFilter!:
    ((data: AutocompleteOption | string) => string) | null;
  @Input() title = '';
  @Input() freeText = false;
  @Input() cleanAfterSelect = false;
  @Input() loadingList = false;
  @Input() errorMessages!: any;
  @Input() floatLabelStatus: FloatLabelType = 'auto';
  @Input() hideSingleSelectionIndicator = false;
  @Input() disabled = false;
  @Input() isReadOnly = false;
  @Input() applyTitleCase = true;
  @Input() hintTemplate?: TemplateRef<unknown>;
  private _parentControl?: FormControl;
  @Input() set parentControl(control: FormControl | undefined) {
    this._parentControl = control;
    this.syncRequiredMarker();
  }
  get parentControl(): FormControl | undefined {
    return this._parentControl;
  }
  @Input() optionList: (AutocompleteOption | string)[] = [];
  @Input() pasteCallback?: (value: string) => Observable<any[]>;
  @Input() optionHtml?: (option: any) => string;
  @Input() noOptionsMessage = '';

  @Input() control: FormControl = new FormControl('');

  errorMatcher: ErrorStateMatcher = new ErrorStateMatcher();

  filteredOptions!: any[];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control'] || changes['optionList']) {
      this.checkInitialData();
      this.filter();
    }

    if (changes['control']) {
      this.syncRequiredMarker();
    }

    if (changes['disabled']) {
      this.disabled ? this.control.disable() : this.control.enable();
    }

    if (changes['parentControl'] && this.parentControl) {
      this.errorMatcher = new ParentControlErrorStateMatcher(
        this.parentControl
      );
    }
  }

  get errorKey(): string {
    return Object.keys(this.control.errors || {})[0];
  }

  get parentErrorKey(): string {
    return Object.keys(this.parentControl?.errors ?? {})[0];
  }

  // The field is required when either its own control or the parent control
  // carries the required validator.
  get required(): boolean {
    return (
      this.control.hasValidator(Validators.required) ||
      (this.parentControl?.hasValidator(Validators.required) ?? false)
    );
  }

  private syncRequiredMarker(): void {
    const input = this._matInput;
    if (!input) return;

    Promise.resolve().then(() => {
      input.required = this.required;
      input.stateChanges.next();
    });
  }

  // Marks the field as touched on blur to display validation errors.
  onBlur(): void {
    this.control.markAsTouched();
    this.parentControl?.markAsTouched();
  }

  get showNoOptionsMessage(): boolean {
    return !!this.noOptionsMessage && this.filteredOptions?.length === 0;
  }

  checkInitialData() {
    if (this.control?.value && this.optionList?.length > 0 && !this.freeText) {
      const foundObject = this.foundSelected(this.control.value);
      if (!foundObject) {
        this.resetText();
      }
    }
  }

  displayValue = (value: any): string => {
    value = this.selectedValue(value);

    if (!value) return '';
    if (this.customFilter) {
      return this.customFilter(value);
    }
    return this.attrFilter && value[this.attrFilter]
      ? value[this.attrFilter]
      : value.toString();
  };

  selectedValue(value: unknown) {
    if (!value) return '';
    if (this.attrToSave && typeof value !== 'object') {
      const savedValue = this.foundSelected(value);
      return savedValue ?? value;
    }
    return value;
  }

  foundSelected(value: unknown) {
    return this.optionList.find(obj => {
      return lodash.isEqual(
        this.attrToSave ? (obj as AutocompleteOption)[this.attrToSave] : obj,
        value
      );
    });
  }

  filter(): void {
    if (this.autocompleteInput) {
      const filterValue =
        this.autocompleteInput.nativeElement.value.toLowerCase();

      this.filteredOptions = this.optionList.filter(option =>
        this.displayValue(option)
          .toLowerCase()
          .includes(filterValue.toLocaleLowerCase())
      );
    }
  }

  optionSelected(event: MatAutocompleteSelectedEvent) {
    const value = event.option.value;
    this.valueChange.emit(value);
    if (this.cleanAfterSelect) {
      this.resetText();
    }
  }

  paste(event: ClipboardEvent) {
    if (!this.pasteCallback) {
      return;
    }

    const pastedData = event.clipboardData?.getData('text/plain');
    if (!pastedData) {
      return;
    }

    event.preventDefault();
    this.control.setValue(pastedData, { emitEvent: false });
    this.pasteCallback(pastedData)
      .pipe(catchError(() => of([])))
      .subscribe(values => {
        values.forEach(value => this.valueChange.emit(value));
        this.resetText();
      });
  }

  resetText() {
    this.control.setValue('');
    this.autocompleteInput && (this.autocompleteInput.nativeElement.value = '');
    this.filter();
  }

  onEnter(event: Event) {
    if (this.freeText) {
      event.preventDefault();
      event.stopPropagation();
      this.autocompleteTrigger?.closePanel();
    }

    this.emitAndClean();
  }

  emitAndClean() {
    if (this.freeText) {
      this.valueChange.emit(this.control.value);

      if (this.cleanAfterSelect) {
        this.resetText();
      }
    }
  }
}
